/**
 * WizardHat - 3D Rotating Wizard Hat Component
 * Uses BufferGeometry for modern Three.js compatibility
 */

import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";

function WizardHat({ canvasStyle = {}, ...rest }) {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const [webGLError, setWebGLError] = React.useState(false);

  useEffect(() => {
    function createWizardHat() {
      const container = containerRef.current;
      const canvas = canvasRef.current;

      if (!container || !canvas) return;

      function hasWebGL() {
        try {
          const testCanvas = document.createElement('canvas');
          const gl = testCanvas.getContext("webgl") || testCanvas.getContext("experimental-webgl");
          return gl && gl instanceof WebGLRenderingContext;
        } catch (e) {
          return false;
        }
      }

      if (!hasWebGL()) {
        setWebGLError(true);
        return;
      }

      function makeMagic() {
        const { width, height } = container.getBoundingClientRect();

        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
        camera.position.z = 280;
        camera.position.y = 30;

        let renderer;
        try {
          renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
          renderer.setSize(width, height);
          renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
          renderer.setClearColor(0x000000, 0);
        } catch (error) {
          setWebGLError(true);
          return;
        }

        const points = [];

        // Cone
        for (let i = 0; i < 40; i++) {
          const t = i / 40, y = t * 120 - 35, radius = 55 * (1 - t * 0.92);
          for (let j = 0; j < 60; j++) {
            const angle = (j / 60) * Math.PI * 2;
            if (radius > 3) points.push(Math.cos(angle) * radius, y, Math.sin(angle) * radius);
          }
        }

        // Brim
        for (let i = 0; i < 18; i++) {
          const radius = 48 + (i / 18) * 42, y = -35 + Math.sin((i / 18) * Math.PI) * 5;
          for (let j = 0; j < 70; j++) {
            const angle = (j / 70) * Math.PI * 2;
            points.push(Math.cos(angle) * radius, y, Math.sin(angle) * radius);
          }
        }

        // Band
        for (let j = 0; j < 55; j++) {
          const angle = (j / 55) * Math.PI * 2;
          points.push(Math.cos(angle) * 44, -8, Math.sin(angle) * 44);
          points.push(Math.cos(angle) * 44, -4, Math.sin(angle) * 44);
        }

        // Sparkles
        for (let i = 0; i < 100; i++) {
          const angle = Math.random() * Math.PI * 2, r = 35 + Math.random() * 75;
          points.push(Math.cos(angle) * r, -55 + Math.random() * 160, Math.sin(angle) * r);
        }

        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute("position", new THREE.Float32BufferAttribute(points, 3));
        const material = new THREE.PointsMaterial({ color: 0x989db5, size: 1.5, sizeAttenuation: true });
        const hatPoints = new THREE.Points(geometry, material);

        hatPoints.rotation.x = 0.12;
        hatPoints.rotation.z = 0.06;
        scene.add(hatPoints);

        container.classList.add("peekaboo");

        const controls = new OrbitControls(camera, canvas);
        controls.enableKeys = false;
        controls.enablePan = false;
        controls.enableZoom = false;
        controls.enableDamping = true;
        controls.dampingFactor = 0.05;
        controls.enableRotate = true;
        controls.autoRotate = true;
        controls.autoRotateSpeed = 1.5;

        let animationId;
        function animate() {
          controls.update();
          renderer.render(scene, camera);
          animationId = requestAnimationFrame(animate);
        }
        animate();

        function handleResize() {
          const { width: w, height: h } = container.getBoundingClientRect();
          if (w && h) { camera.aspect = w / h; camera.updateProjectionMatrix(); renderer.setSize(w, h); }
        }
        window.addEventListener("resize", handleResize);

        return () => {
          window.removeEventListener("resize", handleResize);
          cancelAnimationFrame(animationId);
          geometry.dispose();
          material.dispose();
          renderer.dispose();
          controls.dispose();
        };
      }

      try {
        if (hasWebGL()) return makeMagic();
        else setWebGLError(true);
      } catch (error) {
        setWebGLError(true);
      }
    }

    const cleanup = createWizardHat();
    return cleanup;
  }, []);

  if (webGLError) {
    return (
      <div
        style={{
          display: "flex", alignItems: "center", justifyContent: "center",
          width: "100%", height: "100%",
          background: "linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #7c3aed 100%)",
          borderRadius: 20, position: "relative", overflow: "hidden",
          ...canvasStyle,
        }}
        {...rest}
      >
        <div style={{
          position: "absolute", inset: 0,
          background: `
            radial-gradient(2px 2px at 20% 30%, #fff, transparent),
            radial-gradient(2px 2px at 60% 70%, rgba(255,255,255,0.8), transparent),
            radial-gradient(1px 1px at 40% 40%, #fff, transparent),
            radial-gradient(2px 2px at 80% 20%, rgba(245,158,11,0.8), transparent),
            radial-gradient(1px 1px at 30% 80%, #fff, transparent)
          `,
          opacity: 0.6, pointerEvents: "none",
        }} />
        <div style={{ textAlign: "center", color: "#fff", zIndex: 2, padding: "1rem" }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
            <path d="M12 2L8.5 14H4L3 18h18l-1-4h-4.5L12 2z" fill="#fff" opacity="0.9"/>
            <path d="M3 18l0.5 2c0.2 0.8 0.8 1.2 1.5 1.2h14c0.7 0 1.3-0.4 1.5-1.2L21 18H3z" fill="#fff" opacity="0.6"/>
            <circle cx="14" cy="5" r="0.8" fill="#21d4fd"/>
            <circle cx="10" cy="8" r="0.6" fill="#21d4fd" opacity="0.7"/>
          </svg>
          <div style={{ fontSize: "1.1rem", fontWeight: 700, marginTop: 8 }}>Wyzcon</div>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} {...rest}>
      <canvas ref={canvasRef} width="700" height="600" style={{ outline: "none", ...canvasStyle }} />
    </div>
  );
}

WizardHat.propTypes = {
  canvasStyle: PropTypes.objectOf(PropTypes.any),
};

export default WizardHat;
